import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Lottie from "lottie-react";
import images from "../../assets/Icons/Icons";
import appHeaderLogo from "../../assets/Icons/appHeaderLogo.png";
import settingsIcon from "../../assets/jsons/settings.json";
import { triggerLogout } from "../../auth/logout";
import "./Header.scss";
import ProfileModal from "../../containers/Dashboard/Setting/component/ProfileModal";
import { useSchoolHierarchy } from "../../hooks/useSchoolHierarchy";
import { useCurrentUser } from "../../hooks/useGetUserDetail";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import GradeDropdown from "../CustomDropdown/CustomDropdown";

interface HeaderProps {
  isAdmin?: boolean;
  grade?: string;
  onGradeChange?: any;
  grades?: any[];
}

const Header = ({
  isAdmin = false,
  grade,
  onGradeChange,
  grades,
}: HeaderProps) => {
  const navigate = useNavigate();
  const [showProfile, setShowProfile] = useState(false);
  const { data: userInfo } = useCurrentUser();
  const schoolHash = userInfo?.schoolHash;

  const { data: schoolData } = useSchoolHierarchy({
    schoolHash: schoolHash,
    enabled: !!schoolHash,
  });
  const [openlogoutModal, setOpenLogoutModal] = useState(false);

  const handleLogout = () => triggerLogout("manual");

  const profileRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showProfile &&
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setShowProfile(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showProfile]);

  return (
    <header className="header">
      {/* Left — profile image + name + school */}
      <div className="header-left" ref={profileRef}>
        <div className="header-avatar">
          <img
            src={schoolData?.schoolLogo || images.profileLogo}
            alt="Profile"
          />
        </div>
        <div className="header-info">
          <div className="header-name">{userInfo?.name}</div>
          <div className="header-subtitle">
            <span>{schoolData?.schoolName}</span>
            {isAdmin && (
              <>
                <div
                  style={{
                    width: 12,
                    height: 12,
                    backgroundColor: "#9A8E8E",
                    borderRadius: 50,
                    marginLeft: 4,
                  }}
                />
                <GradeDropdown
                  grades={grades}
                  value={grade}
                  onChange={(val) => onGradeChange?.(val)}
                />
              </>
            )}
          </div>
        </div>

        <ProfileModal
          isOpen={showProfile}
          onClose={() => setShowProfile(false)}
          onSettings={() => navigate("/settings")}
          onHelp={() => console.log("Help")}
          onLogout={() => setOpenLogoutModal(true)}
          userName={userInfo?.name || "User"}
        />

        {openlogoutModal && (
          <ConfirmModal
            isOpen={openlogoutModal}
            title="Confirm Logout"
            message="Are you sure you want to log out?"
            confirmText="Log out"
            cancelText="Cancel"
            onConfirm={handleLogout}
            onCancel={() => setOpenLogoutModal(false)}
          />
        )}
      </div>

      {/* Right — powered-by logo + settings */}
      <div className="header-right">
        <div className="header-brand-wrap">
          <span className="header-powered-by">Powered by</span>
          <img src={appHeaderLogo} className="header-brand" />
        </div>
        <div className="header-divider" />
        <button
          className="header-settings-btn"
          onClick={() => setShowProfile(true)}
          aria-label="Settings"
        >
          <Lottie
            animationData={settingsIcon}
            loop
            autoplay
            style={{ width: 60, height: 60 }}
          />
        </button>
      </div>
    </header>
  );
};

export default Header;
